// Copyright (c) 2026 AesirX.io Ltd. All rights reserved.
// This source code is proprietary and confidential.
// See LICENSE.md for licensing terms.
export const DATA_EXPLOITATION_WORDS = {
    // ── Generic values ──────────────────────────────────────────
    none: { en: '—', vi: '—' },
    yes: { en: 'Yes', vi: 'Có' },
    no: { en: 'No', vi: 'Không' },
    notStated: { en: 'Not stated', vi: 'Chưa nêu' },
    notRecorded: { en: 'Not recorded', vi: 'Chưa ghi nhận' },
    fieldNotes: { en: 'Notes', vi: 'Ghi chú' },
    referenceNotFound: { en: 'No longer found', vi: 'Không còn tìm thấy' },
    evidenceFiles: { en: 'Evidence files', vi: 'Tệp tài liệu chứng minh' },
    evidenceFilesNone: { en: 'No file bound', vi: 'Chưa gắn tệp nào' },
    evidenceFileHash: { en: 'SHA-256 {hash}', vi: 'SHA-256 {hash}' },
    evidenceFileNotHeld: { en: 'File no longer held', vi: 'Tệp không còn được lưu giữ' },
    /**
     * Remediation X (PA-5): printed in place of a pack label the page or the
     * export could not read (the register context failed, or the installed
     * template no longer declares the value). Never the code itself.
     */
    labelNotReadable: { en: 'Label could not be read', vi: 'Không đọc được nhãn' },
    /** Remediation X (PA-5): the dispositions KPI for retained data, whatever the field label reads. */
    kpiRetainedUnderLaw: {
        en: 'Kept under a provision of law',
        vi: 'Lưu giữ theo quy định của pháp luật',
    },
    /**
     * Remediation X (G1-8): a linked record of another register whose name is
     * not shown because the viewer does not hold that register's read permission.
     */
    linkWithheld: {
        en: 'Linked record (name not shown: you do not hold the read permission of its register)',
        vi: 'Hồ sơ được liên kết (không hiển thị tên: bạn không có quyền xem sổ chứa hồ sơ này)',
    },
    // ── Register notices ────────────────────────────────────────
    noDeclaringFramework: {
        en: 'No installed framework declares this register, so no record can be created. This describes the content installed for your organization, not whether your organization holds the duty.',
        vi: 'Không có khung tuân thủ đã cài đặt nào khai báo sổ này nên không thể tạo hồ sơ. Câu này mô tả nội dung đã cài đặt cho tổ chức bạn, không phải việc tổ chức bạn có nghĩa vụ hay không.',
    },
    /**
     * Remediation Y (legal pass 2): the statement is the organization's, not a
     * finding — the legal reader's wording, never "this is not your
     * organization's duty".
     */
    applicabilityOutOfScope: {
        en: 'On your organization’s stated routes, this duty does not apply. This rests on your organization’s statement, not a finding. The register stays available for records your organization chooses to keep.',
        vi: 'Theo cơ chế tổ chức đã khai báo, nghĩa vụ này không áp dụng. Điều này dựa trên khai báo của tổ chức, không phải một kết luận. Sổ vẫn sẵn sàng cho những hồ sơ tổ chức bạn muốn lưu giữ.',
    },
    /**
     * Remediation W (U18): the same reading on a register that is NOT offered
     * off the stated routes (the incubator register), where "stays available"
     * would contradict the *Not offered* notice beneath it.
     */
    applicabilityOutOfScopeNotOffered: {
        en: 'On your organization’s stated routes, this duty does not apply. This rests on your organization’s statement, not a finding. The register is not offered for new records.',
        vi: 'Theo cơ chế tổ chức đã khai báo, nghĩa vụ này không áp dụng. Điều này dựa trên khai báo của tổ chức, không phải một kết luận. Sổ không mở để ghi nhận hồ sơ mới.',
    },
    applicabilityProfileNotStated: {
        en: 'Whether this duty reaches your organization depends on a route determination: your organization’s data exploitation routes are not stated, or are stated as not known.',
        vi: 'Việc nghĩa vụ này có áp dụng cho tổ chức bạn hay không phụ thuộc vào việc xác định cơ chế: cơ chế khai thác dữ liệu của tổ chức bạn chưa được khai báo, hoặc được khai báo là chưa xác định được.',
    },
    applicabilityNotDeclared: {
        en: 'The installed framework does not state who carries this duty, so the platform does not say whether it reaches your organization.',
        vi: 'Khung tuân thủ đã cài đặt không nêu ai mang nghĩa vụ này, nên nền tảng không nêu nghĩa vụ có áp dụng cho tổ chức bạn hay không.',
    },
    applicabilityReference: {
        en: 'The framework that declares this register is installed for reference. Nothing is recorded here under a reference install.',
        vi: 'Khung tuân thủ khai báo sổ này được cài đặt để tham khảo. Khi cài đặt để tham khảo, không ghi nhận nội dung nào tại đây.',
    },
    clockNotLiveIntro: {
        en: 'An installed period for this register is not in force yet:',
        vi: 'Một thời hạn đã cài đặt cho sổ này chưa có hiệu lực:',
    },
    notYetInForce: { en: 'Not yet in force', vi: 'Chưa có hiệu lực' },
    /**
     * Remediation X (PA-1): the declaring framework's own effective date, shown
     * on every register page and export whatever the linked periods say.
     */
    frameworkAppliesFrom: {
        en: 'The installed framework applies from {date}.',
        vi: 'Khung tuân thủ đã cài đặt được áp dụng từ {date}.',
    },
    frameworkActivationNotStated: {
        en: 'The installed framework states no date from which it applies.',
        vi: 'Khung tuân thủ đã cài đặt không nêu ngày bắt đầu áp dụng.',
    },
    frameworkActivationUnreadable: {
        en: 'When the installed framework applies could not be read.',
        vi: 'Không đọc được thời điểm khung tuân thủ đã cài đặt bắt đầu áp dụng.',
    },
    /**
     * Merged with `main` (Sprint 63.31.0, #423): a declaring framework that applies
     * from a phase it does not date is not treated as in force.
     */
    frameworkActivationPhaseUnresolved: {
        en: 'The installed framework applies from a phase it does not date, so it is not treated as in force.',
        vi: 'Khung tuân thủ đã cài đặt áp dụng từ một giai đoạn mà khung không nêu ngày, nên không được coi là đang có hiệu lực.',
    },
    /**
     * Remediation X (PA-1): a requirement reading qualified while its framework
     * is upcoming. Remediation Y (P2-2): the date belongs to when the framework
     * applies, its own sentence — never attached to "does not apply" (which read
     * "until") or to "stated" (which read "stated since").
     */
    presentationFromDate: {
        en: '{presentation}. The installed framework applies from {date}.',
        vi: '{presentation}. Khung tuân thủ đã cài đặt được áp dụng từ {date}.',
    },
    // ── Route determination (Slice A, AC1, AC2) ─────────────────
    notDetermined: {
        en: 'Not determined: no route determination is recorded. This is not a statement that the organization is out of scope.',
        vi: 'Chưa xác định: chưa ghi nhận việc xác định cơ chế nào. Câu này không có nghĩa là tổ chức nằm ngoài phạm vi áp dụng.',
    },
    determinationRecorded: { en: 'Recorded', vi: 'Đã ghi nhận' },
    noRoute: {
        en: 'None of the routes (a person’s determination)',
        vi: 'Không sử dụng cơ chế nào (do người có thẩm quyền xác định)',
    },
    routesNotInStatement: {
        en: 'This activity names a route your organization’s statement does not include. This decides nothing; the statement is changed only on the organization profile.',
        vi: 'Hoạt động này nêu một cơ chế không có trong khai báo của tổ chức bạn. Thông báo này không quyết định điều gì; khai báo chỉ được thay đổi tại hồ sơ tổ chức.',
    },
    statementStated: { en: 'Stated', vi: 'Đã khai báo' },
    statementNone: {
        en: 'Stated: the organization uses none of the routes',
        vi: 'Đã khai báo: tổ chức không sử dụng cơ chế nào',
    },
    statementNotStated: { en: 'Not stated', vi: 'Chưa khai báo' },
    statementUnknown: {
        en: 'Stated as not known',
        vi: 'Khai báo là chưa xác định được',
    },
    presentationAppliesOnStatedRoutes: {
        en: 'Applies on your stated routes',
        vi: 'Áp dụng theo cơ chế tổ chức đã khai báo',
    },
    /**
     * A requirement the pack declares applicable with no route condition. Owner
     * ruling #663: a duty arises from the activity, never from installing the
     * pack, so the words say the requirement does not turn on the stated routes
     * (S4L-C3). The key keeps its name.
     */
    presentationAppliesToEveryInstaller: {
        en: 'Applies whatever routes your organization states',
        vi: 'Áp dụng không phụ thuộc vào cơ chế tổ chức đã khai báo',
    },
    presentationDependsOnRouteDetermination: {
        en: 'Depends on a route determination',
        vi: 'Phụ thuộc vào việc xác định cơ chế',
    },
    presentationDoesNotApplyOnStatedRoutes: {
        en: 'Does not apply on your stated routes',
        vi: 'Không áp dụng theo cơ chế tổ chức đã khai báo',
    },
    presentationCarriedForReference: {
        en: 'Carried for reference by the installed framework',
        vi: 'Được khung tuân thủ đã cài đặt nêu để tham khảo',
    },
    presentationNotDeclared: {
        en: 'The installed framework states no applicability',
        vi: 'Khung tuân thủ đã cài đặt không nêu phạm vi áp dụng',
    },
    // ── Requests and grants (Slice B, AC3, AC4) ─────────────────
    requestDraft: { en: 'Draft', vi: 'Bản nháp' },
    requestSent: {
        en: 'Sent by the organization',
        vi: 'Tổ chức đã gửi',
    },
    outcomeGrant: { en: 'Granted by the authority', vi: 'Được cơ quan có thẩm quyền chấp thuận' },
    outcomeRefusal: { en: 'Refused by the authority', vi: 'Bị cơ quan có thẩm quyền từ chối' },
    outcomeNotRecorded: {
        en: 'No result recorded from the authority’s document',
        vi: 'Chưa ghi nhận kết quả theo văn bản của cơ quan có thẩm quyền',
    },
    authoritysClock: {
        en: 'The authority’s clock: {amount} {unit}. It binds the authority; no due date is computed.',
        vi: 'Thời hạn của cơ quan có thẩm quyền: {amount} {unit}. Thời hạn này ràng buộc cơ quan có thẩm quyền; nền tảng không tính ngày đến hạn.',
    },
    centresPeriod: {
        en: 'The Centre’s period: {amount} {unit}. It binds the Centre; no due date is computed.',
        vi: 'Thời hạn của Trung tâm: {amount} {unit}. Thời hạn này ràng buộc Trung tâm; nền tảng không tính ngày đến hạn.',
    },
    noNumericPeriod: {
        en: 'No numeric period is stated. No countdown is shown.',
        vi: 'Không quy định thời hạn bằng con số. Không hiển thị đếm ngược.',
    },
    statedMaximum: {
        en: 'At most {amount} {unit}, as stated. This is a maximum, never the approved term.',
        vi: 'Tối đa {amount} {unit}, theo quy định. Đây là mức tối đa, không phải thời hạn được chấp thuận.',
    },
    grantNotEnded: { en: 'Not ended', vi: 'Chưa kết thúc' },
    grantTermEnded: {
        en: 'Approved term ended on {date}',
        vi: 'Thời hạn được chấp thuận đã kết thúc ngày {date}',
    },
    grantPurposeEnded: {
        en: 'Purpose marked ended on {date}',
        vi: 'Mục đích được ghi nhận đã kết thúc ngày {date}',
    },
    dispositionNotRecorded: {
        en: 'Disposition not recorded',
        vi: 'Chưa ghi nhận việc xử lý dữ liệu khi kết thúc',
    },
    dispositionRecorded: {
        en: 'Disposition recorded',
        vi: 'Đã ghi nhận việc xử lý dữ liệu khi kết thúc',
    },
    /**
     * Remediation W (U9): the disposition duty is not inapplicable before a grant
     * ends — it is not yet due. A request that is not a grant has nothing to
     * dispose of; a grant whose term runs has not reached its end.
     */
    dispositionNotAGrant: { en: 'No grant recorded', vi: 'Chưa ghi nhận việc chấp thuận' },
    dispositionTermRunning: {
        en: 'Grant not ended: no disposition is due yet',
        vi: 'Việc chấp thuận chưa kết thúc: chưa đến lúc xử lý dữ liệu khi kết thúc',
    },
    /** Sprint 62.51.0 (pack 1.2.1) — a grant's access, read from the right actions recorded on it. */
    // Remediation Y (legal pass 2): an ending decision ends one decision, so
    // the words say no suspension or revocation stands, never "the latest
    // decision". "Đình chỉ" is the decree's word (Điều 17 khoản 3) and the
    // pack's label (S4L-C3).
    accessGranted: {
        en: 'Access as granted: no temporary halt, suspension or revocation recorded on this grant stands without its recorded ending',
        vi: 'Quyền khai thác theo việc chấp thuận: không có quyết định tạm dừng, đình chỉ hoặc thu hồi nào đã ghi nhận đối với việc chấp thuận này mà chưa ghi nhận việc chấm dứt',
    },
    /** Remediation Y (P3-1): the grant has ended, so it gives no access whatever its right actions say. */
    accessEndedTerm: {
        en: 'No access: the approved term ended on {date}',
        vi: 'Không còn cho phép khai thác: thời hạn được chấp thuận đã kết thúc ngày {date}',
    },
    accessEndedPurpose: {
        en: 'No access: the purpose was marked ended on {date}',
        vi: 'Không còn cho phép khai thác: mục đích được ghi nhận đã kết thúc ngày {date}',
    },
    /** Remediation Y: printed after the withdrawal words when more than one withdrawal stands. */
    accessWithdrawnStandingCount: {
        en: '{count} temporary halt, suspension or revocation decisions recorded on this grant stand; each ending decision ends one of them.',
        vi: 'Có {count} quyết định tạm dừng, đình chỉ hoặc thu hồi đã ghi nhận đối với việc chấp thuận này chưa được chấm dứt; mỗi quyết định chấm dứt chỉ chấm dứt một quyết định.',
    },
    /** Remediation Y: an ending decision that names the decision it ends. */
    rightActionEnds: {
        en: 'ends the decision of {date}{ref}',
        vi: 'chấm dứt quyết định ngày {date}{ref}',
    },
    accessWithdrawn: {
        en: 'Does not give access: {kind} on {date}, recorded from the deciding body’s document',
        vi: 'Không còn cho phép khai thác: {kind} ngày {date}, ghi nhận theo văn bản của cơ quan quyết định',
    },
    /**
     * Remediation X (G1-3, pack 1.2.4): the withdrawal is a final one (a
     * revocation). X2 (pack 1.2.5, owner decision 2026-10-25, LX-2): only a
     * recorded withdrawal or setting-aside of it lifts it (an option whose
     * `ends_withdrawal_by` names its kind). Remediation Z (legal P-3 / R-3): the
     * VI verb for withdrawing the decision is *rút lại*, never *thu hồi*, which
     * is the decision itself.
     */
    accessWithdrawnFinal: {
        en: 'Does not give access: {kind} on {date}, recorded from the deciding body’s document. Access stays withdrawn unless the withdrawal or setting-aside of this decision is recorded; otherwise, exploiting again is recorded as a new request with its own result.',
        vi: 'Không còn cho phép khai thác: {kind} ngày {date}, ghi nhận theo văn bản của cơ quan quyết định. Quyền khai thác vẫn không được cho phép, trừ khi đã ghi nhận việc rút lại hoặc hủy bỏ quyết định này; ngoài trường hợp đó, việc khai thác lại được ghi nhận là một yêu cầu mới với kết quả riêng.',
    },
    // ── Recorded in error (Remediation Y, legal pass 2) ─────────
    // The organization's correction of its own register: never worded as an
    // authority's act (no "withdrawn", "revoked", "set aside", "cancelled",
    // "annulled"; VI no "thu hồi", "hủy bỏ", "bãi bỏ").
    recordedInErrorBadge: {
        en: 'Recorded in error (organization’s correction)',
        vi: 'Ghi nhầm (tổ chức đính chính)',
    },
    /** On a grant's entries (right actions, the end of purpose). */
    recordedInErrorStatementGrant: {
        en: 'Marked as recorded in error by {person} on {date}: {reason}. This corrects this register; it is not a decision of any authority. The entry is kept and no longer counts toward the grant’s access or the record’s state.',
        vi: '{person} đánh dấu là ghi nhầm ngày {date}: {reason}. Đây là việc đính chính sổ theo dõi, không phải quyết định của cơ quan có thẩm quyền. Mục này được giữ lại và không còn được tính vào tình trạng khai thác của việc chấp thuận hoặc tình trạng của hồ sơ.',
    },
    /** On every other record's entries (extensions, the Centre's acts, plan versions, certificates, checks, reliances). */
    recordedInErrorStatementRecord: {
        en: 'Marked as recorded in error by {person} on {date}: {reason}. This corrects this register; it is not a decision of any authority. The entry is kept and no longer counts toward the record’s state.',
        vi: '{person} đánh dấu là ghi nhầm ngày {date}: {reason}. Đây là việc đính chính sổ theo dõi, không phải quyết định của cơ quan có thẩm quyền. Mục này được giữ lại và không còn được tính vào tình trạng của hồ sơ.',
    },
    /** The dialog's note (legal condition b, c): what a correction is not, and how a mistaken date is corrected. */
    recordedInErrorDialogNote: {
        en: 'Use this only when your organization recorded the entry by mistake. If a decision was withdrawn, ended or set aside — by the body that made it, a body deciding a complaint, or a court — record that decision instead. To correct a date, mark this entry and record the right one; both are kept.',
        vi: 'Chỉ sử dụng khi tổ chức bạn đã ghi nhận mục này do nhầm lẫn. Nếu quyết định đã được cơ quan ban hành rút lại hoặc chấm dứt, hoặc bị cơ quan giải quyết khiếu nại hay Tòa án hủy bỏ, hãy ghi nhận quyết định đó. Để sửa ngày, hãy đánh dấu mục này và ghi nhận mục đúng; cả hai đều được giữ lại.',
    },
    recordedInErrorFinal: {
        en: 'A correction cannot be undone. If the entry was right, record it again.',
        vi: 'Không thể hoàn tác việc đính chính. Nếu mục đã ghi là đúng, hãy ghi nhận lại.',
    },
    colRecordedInError: { en: 'Recorded in error', vi: 'Ghi nhầm' },
    noDeadlineAfterEnd: {
        en: 'No deadline after the end is computed.',
        vi: 'Nền tảng không tính thời hạn nào sau thời điểm kết thúc.',
    },
    restingRecordsHeading: {
        en: 'Records resting on this grant',
        vi: 'Các hồ sơ dựa trên việc chấp thuận này',
    },
    // ── Controlled testing (Slice C, AC7–AC11) ──────────────────
    carryOverNotChecked: { en: 'Carry-over not checked', vi: 'Chưa kiểm tra việc chuyển tiếp' },
    carryOverChecked: { en: 'Carry-over checked', vi: 'Đã kiểm tra việc chuyển tiếp' },
    /** Remediation W (U16): the latest check records a kind that is no separate authority. */
    carryOverCheckedNoAuthority: {
        en: 'Carry-over checked: no separate authority held, so the carry-over waits',
        vi: 'Đã kiểm tra việc chuyển tiếp: chưa có căn cứ riêng nên việc chuyển tiếp phải chờ',
    },
    carryOverNotApplicable: {
        en: 'No completion certificate recorded',
        vi: 'Chưa ghi nhận giấy chứng nhận kết thúc hoạt động thử nghiệm có kiểm soát',
    },
    reuseCheckNoGate: {
        en: 'A recommended practice: the instrument states no such gate. The check records the separate authority for the use and who checked it.',
        vi: 'Thực tiễn được khuyến nghị: văn bản không quy định bước kiểm soát này. Việc kiểm tra ghi nhận căn cứ riêng cho việc sử dụng và người đã kiểm tra.',
    },
    certificateFromAuthorityCopy: {
        en: 'Recorded from the authority’s copy. The platform issues no certificate.',
        vi: 'Được ghi nhận theo bản của cơ quan có thẩm quyền. Nền tảng không cấp giấy chứng nhận.',
    },
    completionNotProduction: {
        en: 'A completion certificate is not an authorization for production use.',
        vi: 'Giấy chứng nhận kết thúc hoạt động thử nghiệm có kiểm soát không phải là sự cho phép sử dụng trong môi trường vận hành chính thức.',
    },
    certificateParticipation: { en: 'Participation certificate', vi: 'Giấy chứng nhận tham gia' },
    certificateCompletion: {
        en: 'Completion certificate',
        vi: 'Giấy chứng nhận kết thúc hoạt động thử nghiệm có kiểm soát',
    },
    riskPlanVersion: {
        en: 'Risk-management plan, version {n}',
        vi: 'Phương án quản trị rủi ro, phiên bản {n}',
    },
    extensionRecorded: {
        en: 'Extended to {date} ({ref})',
        vi: 'Gia hạn đến ngày {date} ({ref})',
    },
    extensionNone: { en: 'No extension recorded', vi: 'Chưa ghi nhận gia hạn' },
    centreAction: { en: '{kind} on {date} ({ref})', vi: '{kind} ngày {date} ({ref})' },
    // ── Notices (Slice D, AC12) ─────────────────────────────────
    noticeRecorded: { en: 'Notice recorded', vi: 'Đã ghi nhận thông báo' },
    noticeNotRecorded: { en: 'No notice recorded', vi: 'Chưa ghi nhận thông báo' },
    // ── Foreign cooperation (Slice H, AC21) ─────────────────────
    assessmentNotDeployed: { en: 'Not recorded as deployed', vi: 'Chưa ghi nhận là đã triển khai' },
    assessmentBeforeDeployment: {
        en: 'Assessment recorded before deployment',
        vi: 'Đã ghi nhận đánh giá trước khi triển khai',
    },
    assessmentNotRecordedBeforeDeployment: {
        en: 'Assessment not recorded before deployment',
        vi: 'Chưa ghi nhận đánh giá trước khi triển khai',
    },
    /**
     * Remediation X (PA-1): deployed before the declaring framework applied, so
     * no assessment was due then; not counted as a gap.
     */
    assessmentDeployedBeforeFrameworkEffective: {
        en: 'Deployed before the requirement applied (from {date})',
        vi: 'Triển khai trước khi yêu cầu được áp dụng (từ {date})',
    },
    measuresRecorded: { en: 'Measures recorded', vi: 'Đã ghi nhận biện pháp' },
    measuresNotRecorded: { en: 'Measures not recorded', vi: 'Chưa ghi nhận biện pháp' },
    assessmentNotADossier: {
        en: 'This assessment does not replace a personal-data transfer dossier.',
        vi: 'Việc đánh giá này không thay thế hồ sơ chuyển dữ liệu cá nhân ra nước ngoài.',
    },
    // ── Incubator (Slice F) ─────────────────────────────────────
    incubatorNotStated: {
        en: 'Not offered: your organization’s data exploitation routes are not stated.',
        vi: 'Không áp dụng: cơ chế khai thác dữ liệu của tổ chức bạn chưa được khai báo.',
    },
    incubatorNotOnStatedRoutes: {
        en: 'Not offered: your organization’s stated routes do not include the route this record belongs to.',
        vi: 'Không áp dụng: cơ chế tổ chức bạn đã khai báo không bao gồm cơ chế mà hồ sơ này thuộc về.',
    },
    incubatorNotDeclared: {
        en: 'Not offered: the installed framework does not state which route this record belongs to.',
        vi: 'Không áp dụng: khung tuân thủ đã cài đặt không nêu hồ sơ này thuộc cơ chế nào.',
    },
    /** Remediation W (U21): the liability-reliance sub-record. Never "exempt" or "applies". */
    liabilityRelianceNone: {
        en: 'No reliance on the liability exemption recorded',
        vi: 'Chưa ghi nhận việc dựa vào quy định miễn trách nhiệm',
    },
    liabilityRelianceStatement: {
        en: 'Your organization’s statement of the case it relies on, recorded on {date} as decided by {name}. It is not a finding that the exemption applies.',
        vi: 'Tuyên bố của tổ chức bạn về trường hợp tổ chức dựa vào, được ghi nhận ngày {date} theo quyết định của {name}. Đây không phải là kết luận rằng quy định miễn trách nhiệm được áp dụng.',
    },
    colLiabilityReliance: {
        en: 'Reliance on the liability exemption',
        vi: 'Việc dựa vào quy định miễn trách nhiệm',
    },
    // ── Boundary ────────────────────────────────────────────────
    boundaryHeading: {
        en: 'Scope and boundary — from your installed frameworks',
        vi: 'Phạm vi và ranh giới — theo khung tuân thủ đã cài đặt',
    },
    boundaryFrame: {
        en: 'These statements come from the frameworks installed for your organization, shown as each framework states them. The platform records what your organization did and what the authority’s documents say; it connects to no data centre or portal, submits no request, grants or approves no access, admits no one to controlled testing and certifies nothing.',
        vi: 'Các tuyên bố này đến từ khung tuân thủ đã cài đặt cho tổ chức bạn và được hiển thị đúng như từng khung nêu. Nền tảng ghi nhận những gì tổ chức bạn đã thực hiện và nội dung văn bản của cơ quan có thẩm quyền; nền tảng không kết nối tới trung tâm dữ liệu hay cổng thông tin nào, không gửi yêu cầu, không chấp thuận hay phê duyệt quyền truy cập, không tiếp nhận tham gia thử nghiệm có kiểm soát và không chứng nhận bất cứ điều gì.',
    },
    noBoundaryStatements: {
        en: 'No installed framework declares a boundary statement for this register. This describes the content installed for your organization, not your organization’s scope.',
        vi: 'Không có khung tuân thủ đã cài đặt nào khai báo tuyên bố về ranh giới cho sổ này. Câu này mô tả nội dung đã cài đặt cho tổ chức bạn, không phải phạm vi của tổ chức bạn.',
    },
    // ── Units of a stated period (the installed record's unit code) ──
    unitHours: { en: 'hours', vi: 'giờ' },
    unitDays: { en: 'days', vi: 'ngày' },
    unitWorkingDays: { en: 'working days', vi: 'ngày làm việc' },
    unitCalendarDays: { en: 'calendar days', vi: 'ngày theo lịch' },
    unitMonths: { en: 'months', vi: 'tháng' },
    unitYears: { en: 'years', vi: 'năm' },
    // ── Export frame ────────────────────────────────────────────
    colState: { en: 'State', vi: 'Tình trạng' },
    colRequestState: { en: 'Request state', vi: 'Tình trạng yêu cầu' },
    colDecidedOn: { en: 'Decision date', vi: 'Ngày quyết định' },
    colIncident: { en: 'Incident record', vi: 'Hồ sơ sự cố' },
    colDeletionRun: { en: 'Deletion run', vi: 'Lượt xóa dữ liệu' },
    colVendor: { en: 'Vendor', vi: 'Nhà cung cấp' },
    colTransferDossier: {
        en: 'Personal-data transfer dossier',
        vi: 'Hồ sơ chuyển dữ liệu cá nhân ra nước ngoài',
    },
    colForeignParticipant: { en: 'Foreign participant', vi: 'Bên tham gia nước ngoài' },
    colPurposeEnded: { en: 'Purpose ended', vi: 'Mục đích đã kết thúc' },
    colReuseChecks: {
        en: 'Production-reuse checks',
        vi: 'Kiểm tra việc sử dụng lại cho vận hành chính thức',
    },
    colGrant: { en: 'Grant', vi: 'Việc chấp thuận' },
    colLinkedCase: { en: 'Controlled-testing case', vi: 'Hồ sơ thử nghiệm có kiểm soát' },
    colRegister: { en: 'Register', vi: 'Sổ' },
    colRecord: { en: 'Record', vi: 'Hồ sơ' },
    colDate: { en: 'Date', vi: 'Ngày' },
    colPeriod: { en: 'Period', vi: 'Thời hạn' },
    exportOrganization: { en: 'Organization: {name}', vi: 'Tổ chức: {name}' },
    exportGeneratedAt: { en: 'Generated at: {instant}', vi: 'Thời điểm tạo: {instant}' },
    exportGeneratedBy: { en: 'Generated by: {name}', vi: 'Người tạo: {name}' },
    exportFramework: {
        en: 'Installed framework: {code}, pack version {version}',
        vi: 'Khung tuân thủ đã cài đặt: {code}, phiên bản gói {version}',
    },
    exportRecordCount: { en: 'Records: {count}', vi: 'Số hồ sơ: {count}' },
    exportField: { en: 'Field', vi: 'Trường' },
    exportValue: { en: 'Value', vi: 'Giá trị' },
    exportFrame: {
        en: 'A copy of what your organization recorded in this register, as the register shows it. The platform sends this document to no one.',
        vi: 'Bản sao những gì tổ chức bạn đã ghi nhận trong sổ này, đúng như sổ hiển thị. Nền tảng không gửi tài liệu này cho bất kỳ ai.',
    },
    exportGrantTitle: {
        en: 'Grant and the records resting on it',
        vi: 'Việc chấp thuận và các hồ sơ dựa trên việc chấp thuận',
    },
};
/** Sentence-ending punctuation, Latin and Vietnamese alike (`.`, `!`, `?`, `…`), before any closing quote or bracket. */
const TERMINAL_PUNCTUATION = /[.!?…][)"'”’»\]]*\s*$/;
/**
 * One word in `locale` (`vi`, `vi-VN` → VI; anything else → EN), with each
 * `{name}` filled from `values`. A placeholder with no value stays as written,
 * so a missing value is visible rather than silently dropped.
 */
export function dataExploitationWord(key, locale, values = {}) {
    const pair = DATA_EXPLOITATION_WORDS[key];
    const text = locale === 'vi' || locale.startsWith('vi-') ? pair.vi : pair.en;
    // Remediation Z (walk P3-C): a value the template ends a sentence after
    // (`{reason}.`) keeps its own terminal punctuation, so a free-text reason
    // that already ends in one never prints "..".
    return text.replace(/\{(\w+)\}(\.?)/g, (whole, name, stop) => {
        const value = values[name];
        if (value === undefined)
            return whole;
        if (stop && TERMINAL_PUNCTUATION.test(value))
            return value.trimEnd();
        return value + stop;
    });
}
//# sourceMappingURL=data-exploitation-words.js.map